import { useDataProvider } from 'react-admin';
import { useCallback } from 'react';

export interface KeyState {
  state: 'empty' | 'valid' | 'expired' | 'revoked';
  key_id?: number;
  registered_at?: string;
  expired_at?: string;
  last_used_at?: string;
}

export interface RevokeKeyResponse {
  message: string;
  data: {
    identifier: string;
    previous_state: string;
    current_state: string;
    revoked_at: string;
    revoked_by: {
      id: number;
      name: string;
      email: string;
    };
    reason?: string;
  };
}

export interface RevokeKeysResponse {
  message: string;
  data: {
    succeeded: Array<{ identifier: string; message: string }>;
    failed: Array<{ identifier: string; error: string }>;
    total: number;
    success_count: number;
    failure_count: number;
  };
}

interface UseUnitKeyManagement {
  revokeKey: (identifier: string, reason?: string) => Promise<RevokeKeyResponse>;
  revokeKeys: (identifiers: string[], reason?: string) => Promise<RevokeKeysResponse>;
  getKeyState: (identifier: string) => Promise<KeyState>;
}

export function useUnitKeyManagement(): UseUnitKeyManagement {
  const dataProvider = useDataProvider();

  const revokeKey = useCallback(
    async (identifier: string, reason?: string): Promise<RevokeKeyResponse> => {
      // Use v5 API endpoint directly - dataProvider.post prepends the base API URL (v3/api)
      // so we use ../.. to go back twice (api -> v3 -> root) and access v5/api/unit endpoint
      const response = await dataProvider.post(`../../v5/api/unit/${identifier}/expire_key`, { reason });
      return response;
    },
    [dataProvider],
  );

  const revokeKeys = useCallback(
    async (identifiers: string[], reason?: string): Promise<RevokeKeysResponse> => {
      const response = await dataProvider.post('../../v5/api/units/revoke_keys', { identifiers, reason });
      return response;
    },
    [dataProvider],
  );

  const getKeyState = useCallback(
    async (identifier: string): Promise<KeyState> => {
      const response = await dataProvider.get(`../../v5/api/unit/${identifier}/key_state`);
      return response;
    },
    [dataProvider],
  );

  return {
    revokeKey,
    revokeKeys,
    getKeyState,
  };
}
